import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendUp, Plus, Trash, ClockCounterClockwise } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const STATUSES = ["Pipeline", "Submitted", "Underwriting", "Approved", "Funded", "Declined"];
const navItems = [{ to: "/admin/funding-pipeline", label: "Funding Pipeline", icon: <TrendUp size={18} weight="duotone" />, testId: "nav-admin-funding-pipeline" }];

export default function AdminFundingPipelinePage() {
  const confirmEdit = useEditConfirm();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [deals, setDeals] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ deal_name: "", deal_amount: "", status: "Pipeline", notes: "" });
  const [notesDraft, setNotesDraft] = useState({});
  const [expandedHistory, setExpandedHistory] = useState([]);
  const [dashboardApproval, setDashboardApproval] = useState(null);

  const fetchApproval = async (clientId) => {
    if (!clientId) {
      setDashboardApproval(null);
      return;
    }
    const { data } = await api.get("/admin/client-dashboard/approvals");
    setDashboardApproval(data.find((a) => a.client_id === clientId) || null);
  };

  const buildDashboard = async () => {
    if (!(await confirmEdit(`${selectedClient}'s IGC Dashboard (build)`))) return;
    try {
      await api.post(`/admin/client-dashboard/${selectedClient}/request-approval`);
      toast.success("Dashboard built — awaiting your approval");
      fetchApproval(selectedClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approveDashboard = async () => {
    if (!(await confirmEdit(`${selectedClient}'s IGC Dashboard (approve & publish)`))) return;
    try {
      await api.post(`/admin/client-dashboard/${selectedClient}/approve`);
      toast.success("Published to the client");
      fetchApproval(selectedClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleHistory = (id) => {
    setExpandedHistory((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const fetchClients = async () => {
    const { data } = await api.get("/admin/overview");
    const eligible = data.stores.filter((s) => (s.unlocked_modules || []).includes("funding_pipeline"));
    setClients(eligible);
    setSelectedClient((prev) => prev || (eligible.length > 0 ? eligible[0].client_id : ""));
  };

  const fetchDeals = async (clientId) => {
    if (!clientId) {
      setDeals([]);
      return;
    }
    const { data } = await api.get("/admin/funding-pipeline/deals", { params: { client_id: clientId } });
    setDeals(data);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    fetchDeals(selectedClient);
    fetchApproval(selectedClient);
  }, [selectedClient]);

  const createDeal = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/funding-pipeline/deals", { ...form, client_id: selectedClient, deal_amount: parseFloat(form.deal_amount) || 0 });
      toast.success("Deal added");
      setAddOpen(false);
      setForm({ deal_name: "", deal_amount: "", status: "Pipeline", notes: "" });
      fetchDeals(selectedClient);
      fetchApproval(selectedClient);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const updateStatus = async (id, status) => {
    const d = deals.find((x) => x.id === id);
    if (!(await confirmEdit(`${d?.deal_name || "this deal"}'s status`))) return;
    try {
      await api.patch(`/admin/funding-pipeline/deals/${id}`, { status });
      toast.success("Status updated");
      fetchDeals(selectedClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async (id, current) => {
    const d = deals.find((x) => x.id === id);
    if (!(await confirmEdit(`${d?.deal_name || "this deal"}'s notes`))) return;
    try {
      const value = notesDraft[id] !== undefined ? notesDraft[id] : current;
      await api.patch(`/admin/funding-pipeline/deals/${id}`, { notes: value });
      toast.success("Notes saved");
      fetchDeals(selectedClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteDeal = async (id) => {
    try {
      await api.delete(`/admin/funding-pipeline/deals/${id}`);
      toast.success("Deal removed");
      fetchDeals(selectedClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totalDealValue = deals.reduce((sum, d) => sum + (d.deal_amount || 0), 0);
  const totalCommission = deals.reduce((sum, d) => sum + (d.commission_amount || 0), 0);

  return (
    <DashboardLayout navItems={navItems} moduleName="Funding Pipeline" moduleBackPath="/admin">
      <div data-testid="admin-funding-pipeline-page">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="A dedicated, per-client funding deal pipeline with automatic 20% commission tracking.">Funding Pipeline</h1>
          <Button data-testid="funding-pipeline-add-deal-button" onClick={() => setAddOpen(true)} disabled={!selectedClient} title="Adds a new funding deal for the currently-selected client." className="bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2">
            <Plus size={16} weight="bold" /> Add Deal
          </Button>
        </div>
        <p className="text-igc-muted text-sm mb-6">Dedicated funding deal pipeline with automatic 20% commission — visible only to clients with the Funding Pipeline module unlocked. Referral commissions remain tracked separately in the Sales Reps module.</p>

        <div className="mb-6 max-w-xs">
          <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Client</label>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger data-testid="funding-pipeline-client-select" title="Choose which client's funding deals you want to view or manage." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue placeholder="Select a client" /></SelectTrigger>
            <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {clients.length === 0 && <p className="text-igc-muted text-xs mt-2" data-testid="funding-pipeline-no-client-message">No client currently has the Funding Pipeline module unlocked. Enable it from Store Settings → Module Access Control.</p>}
        </div>

        {selectedClient && (
          <div className="bg-igc-surface border border-igc-gold/20 p-5 mb-8 max-w-lg" data-testid="igc-dashboard-approval-card">
            <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Controls what this client sees on their own dashboard preview — nothing goes live until you approve it.">Your IGC Dashboard — Client-Facing Preview</p>
            {!dashboardApproval && (
              <>
                <p className="text-igc-muted text-sm mb-3">Not built yet. Nothing goes live for {selectedClient} until you explicitly approve it here.</p>
                <Button data-testid="igc-dashboard-build-button" onClick={buildDashboard} title="Builds a preview of this client's dashboard for you to review before it's published." className="bg-igc-gold hover:bg-igc-goldHover text-black">Build & Send for My Approval</Button>
              </>
            )}
            {dashboardApproval?.status === "pending_review" && (
              <>
                <p className="text-igc-goldText text-sm mb-3">Ready for your review. Nothing is visible to {selectedClient} yet.</p>
                <Button data-testid="igc-dashboard-approve-button" onClick={approveDashboard} title="Publishes this dashboard so the client can see it immediately." className="bg-igc-success hover:bg-igc-success/80 text-black">Approve & Publish to Client</Button>
              </>
            )}
            {dashboardApproval?.status === "approved" && (
              <p className="text-igc-success text-sm" data-testid="igc-dashboard-approved-label">Live — {selectedClient} can see Your IGC Dashboard.</p>
            )}
          </div>
        )}

        {selectedClient && (
          <div className="grid grid-cols-2 gap-4 mb-8 max-w-lg">
            <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="funding-pipeline-total-value">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="Combined dollar value of every funding deal on file for this client.">Total Deal Value</p>
              <p className="font-serif text-2xl text-igc-cream">${totalDealValue.toLocaleString()}</p>
            </div>
            <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="funding-pipeline-total-commission">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="20% of the total deal value — IGC's commission on these deals.">Total Commission (20%)</p>
              <p className="font-serif text-2xl text-igc-success">${totalCommission.toLocaleString()}</p>
            </div>
          </div>
        )}

        {selectedClient && deals.length === 0 && <p className="text-igc-muted text-sm">No funding deals on file for this client yet.</p>}
        <div className="space-y-4">
          {deals.map((d) => (
            <div key={d.id} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid={`funding-deal-${d.id}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <p className="font-serif text-lg text-igc-cream">{d.deal_name}</p>
                  <p className="text-igc-muted text-xs">Deal value ${(d.deal_amount || 0).toLocaleString()} — Commission ${(d.commission_amount || 0).toLocaleString()} (20%)</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={d.status} onValueChange={(v) => updateStatus(d.id, v)}>
                    <SelectTrigger data-testid={`funding-deal-status-select-${d.id}`} title="Where this client's funding deal is at in the pipeline." className="w-40 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button data-testid={`funding-deal-history-toggle-${d.id}`} onClick={() => toggleHistory(d.id)} title="Shows or hides every past stage change for this deal, with timestamps." className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText">
                    <ClockCounterClockwise size={16} weight="duotone" /> History ({(d.stage_history || []).length})
                  </button>
                  <button data-testid={`funding-deal-delete-${d.id}`} onClick={() => deleteDeal(d.id)} title="Permanently removes this deal from the pipeline." className="text-igc-muted hover:text-igc-danger transition-colors">
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              </div>
              {expandedHistory.includes(d.id) && (
                <div className="border-l-2 border-igc-gold/30 pl-4 mb-3 space-y-1" data-testid={`funding-deal-history-${d.id}`}>
                  {(d.stage_history || []).map((h, i) => (
                    <p key={i} className="text-igc-muted text-xs">
                      <span className="text-igc-goldText">{h.status}</span> — {formatET(h.changed_at, "MMM d, yyyy h:mm a")}
                    </p>
                  ))}
                </div>
              )}
              <Textarea data-testid={`funding-deal-notes-input-${d.id}`} defaultValue={d.notes} onChange={(e) => setNotesDraft({ ...notesDraft, [d.id]: e.target.value })} placeholder="Notes..." title="Internal notes about this deal, visible only to admins." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-3 min-h-16" />
              <Button data-testid={`funding-deal-notes-save-${d.id}`} onClick={() => saveNotes(d.id, d.notes)} title="Saves the note you just typed." className="bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">Save Notes</Button>
            </div>
          ))}
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="funding-deal-add-modal">
            <DialogHeader><DialogTitle className="font-serif">Add Funding Deal</DialogTitle></DialogHeader>
            <form onSubmit={createDeal} className="space-y-3">
              <Input data-testid="funding-deal-name-input" required placeholder="Deal name" value={form.deal_name} onChange={(e) => setForm({ ...form, deal_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Input data-testid="funding-deal-amount-input" required type="number" min="0" placeholder="Deal amount ($)" value={form.deal_amount} onChange={(e) => setForm({ ...form, deal_amount: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="funding-deal-status-input" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea data-testid="funding-deal-notes-textarea" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Button data-testid="funding-deal-submit-button" type="submit" className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Add Deal</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
